import { unwrapResult } from '@reduxjs/toolkit'
import { Route } from '../../../config/router'
import { Checkout } from '@afterpaytouch/portal-api/types'
import { ConsumerAccountResponse, CrossRegionRedirection } from '../../../model'
import {
  AsyncAction,
  accountLookup,
  afterpayCheckoutLoginTokenSelector,
  cashCheckoutTokenSelector,
  checkPersistentLogin,
  CheckoutAppDispatch,
  oAuth2faHeaders,
} from '../../../state'
import { hasOptedOutRememberMe } from '../../../flow/remember-me/optOut'
import { determinePersistentLoginRoute, InitialiseNextRoute } from '../routing'

type LoadPersistentConsumerResult = ConsumerAccountResponse | Route.VERIFY | null

const isConsumerAccountResponse = (loadPersistentConsumerResult: LoadPersistentConsumerResult): loadPersistentConsumerResult is ConsumerAccountResponse => {
  return loadPersistentConsumerResult !== null && loadPersistentConsumerResult !== Route.VERIFY
}

const loadPersistentConsumer = (): AsyncAction<LoadPersistentConsumerResult> => async (dispatch, getState) => {
  try {
    const state = getState()
    const hasTokenBackedCredentials =
      afterpayCheckoutLoginTokenSelector(state) != null ||
      cashCheckoutTokenSelector(state) != null ||
      Object.values(oAuth2faHeaders(state)).some((value) => value != null)
    if (!hasTokenBackedCredentials && hasOptedOutRememberMe()) {
      return null
    }
    const { active, verify } = unwrapResult(await dispatch(checkPersistentLogin()))
    if (active && verify) {
      return Route.VERIFY
    }
    if (active) {
      return unwrapResult(await dispatch(accountLookup()))
    }
  } catch (e) {
    // Intentionally swallow errors as we return null to indicate no persistent login
  }
  return null
}

export const handlePersistentLogin = async (
  checkout: Checkout,
  dispatch: CheckoutAppDispatch
): Promise<InitialiseNextRoute | CrossRegionRedirection | { route: Route } | null> => {
  const account = await dispatch(loadPersistentConsumer())

  if (account !== null) {
    if (isConsumerAccountResponse(account)) {
      return await determinePersistentLoginRoute({ account, checkout }, dispatch)
    } else {
      return { route: account as Route.VERIFY }
    }
  }

  return null
}
